import type { ReactNode } from "react";
import { Aviso } from "../ui";
import {
  textoDoTempoMedio,
  useIndicadoresDaCarteira,
  type IndicadoresDaCarteira as Dados,
} from "../../nucleo/implantacoes";

/**
 * Os quatro números do topo da carteira (requisito 4 da Amanda, 01/10/2026).
 *
 * Tudo é calculado no servidor, a partir da esteira, do prazo e dos eventos de envio e devolução
 * (`indicadores.py` tem as definições). **Regra do HANDOFF: indicador com dado incompleto não
 * mostra número.** O tempo médio de resposta só existe com respostas medidas suficientes; sem
 * elas aparece "—" com o motivo que o servidor mandou. Enquanto carrega, também "—": um zero
 * provisório seria um número inventado.
 */

function plural(n: number, singular: string, plural_: string) {
  return `${n} ${n === 1 ? singular : plural_}`;
}

function Indicador({
  id,
  rotulo,
  valor,
  explicacao,
  destaque = false,
}: {
  id: string;
  rotulo: string;
  valor: ReactNode;
  explicacao?: ReactNode;
  destaque?: boolean;
}) {
  return (
    <div
      className="cartao"
      data-testid={`indicador-${id}`}
      data-estado={destaque ? "atencao" : undefined}
    >
      <div className="text-[28px] font-semibold numero-tabular" data-testid={`indicador-${id}-valor`}>
        {valor}
      </div>
      <div className="font-medium">{rotulo}</div>
      {explicacao ? (
        <div className="dado-secundario" data-testid={`indicador-${id}-explicacao`}>
          {explicacao}
        </div>
      ) : null}
    </div>
  );
}

export function IndicadoresDaCarteira() {
  const indicadores = useIndicadoresDaCarteira();

  if (indicadores.isError) {
    return (
      <Aviso tom="erro" className="mb-4" data-testid="falha-dos-indicadores">
        Não foi possível calcular os indicadores agora. A carteira abaixo continua valendo.
      </Aviso>
    );
  }

  const d: Dados | undefined = indicadores.data;
  const carregando = d === undefined;

  return (
    <section
      aria-label="Indicadores da carteira"
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-5"
      data-testid="indicadores-da-carteira"
    >
      <Indicador
        id="ativas"
        rotulo="Implantações ativas"
        valor={carregando ? "—" : d.ativas}
      />
      <Indicador
        id="aguardando-infra"
        rotulo="Aguardando a infra"
        valor={carregando ? "—" : d.aguardando_infra}
        explicacao={
          carregando
            ? undefined
            : `${d.infra_com_cliente} com o cliente · ${d.infra_com_geovendas} com a nossa infra`
        }
      />
      <Indicador
        id="risco-de-prazo"
        rotulo="Com risco de prazo"
        valor={carregando ? "—" : d.em_risco_de_prazo}
        destaque={!carregando && d.em_risco_de_prazo > 0}
        explicacao={
          carregando
            ? undefined
            : `Entrega em até ${d.dias_de_alerta} dias ou vencida.${
                d.sem_relogio > 0
                  ? ` ${d.sem_relogio} sem relógio iniciado não ${d.sem_relogio === 1 ? "entra" : "entram"} na conta.`
                  : ""
              }`
        }
      />
      <Indicador
        id="resposta-do-cliente"
        rotulo="Resposta média do cliente"
        valor={carregando ? "—" : textoDoTempoMedio(d.tempo_medio_de_resposta_dias)}
        explicacao={
          carregando
            ? undefined
            : d.tempo_medio_de_resposta_dias === null
              ? (d.motivo_sem_tempo_medio ?? "Sem dados suficientes para a média.")
              : `${plural(d.respostas_medidas, "resposta medida", "respostas medidas")}. ${
                  d.esperas_em_aberto > 0
                    ? `${plural(d.esperas_em_aberto, "espera em aberto fica", "esperas em aberto ficam")} fora da média.`
                    : ""
                }`.trim()
        }
      />
    </section>
  );
}
